import { Operation } from "rfc6902";
import { Endpoints, queryKeys } from "../api";
import { useAuth, useProfile } from "../contexts";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import { AppSettings, CreateMutation, QueryResult, UpdateMutation } from "../models";
import { useMemo } from "react";

export interface AppSettingsQueryParams {
    tenantId?: number | string;
}

export interface IUseAppSettingsQuery {
    myAppSettingsQuery: QueryResult<AppSettings>;
    tenantAppSettingsQuery: QueryResult<AppSettings>;
    createMutation: CreateMutation<AppSettings>;
    updateMutation: UpdateMutation<AppSettings>;
}

export const useAppSettingsQuery = (params?: AppSettingsQueryParams): IUseAppSettingsQuery => {
    const tenantId = isNaN(Number(params?.tenantId)) ? 0 : Number(params?.tenantId);
    const { get, post, patch } = useAxios();
    const { isAuthorized, isRefreshing } = useAuth();
    const { userIsLoading } = useProfile();

    const myAppSettingsQuery = useGenericQuery({
        queryFn: (config) => get<AppSettings>({ url: Endpoints.AppSettings, config }),
        queryKey: queryKeys.appSettings.me(),
        enabled: true,
    });

    const tenantAppSettingsQuery = useGenericQuery({
        queryFn: (config) => get<AppSettings>({ url: `${Endpoints.TenantAppSettings}/${tenantId}`, config }),
        queryKey: queryKeys.appSettings.tenant(tenantId),
        enabled: !!tenantId && isAuthorized && !userIsLoading && !isRefreshing,
    });

    const createMutation = useGenericMutation({
        mutationFn: (data: Partial<AppSettings>) =>
            post<Partial<AppSettings>, AppSettings>({ url: Endpoints.AppSettings, data }),
        queryKey: queryKeys.appSettings.tenant(tenantId),
    });

    const updateMutation = useGenericMutation({
        mutationFn: (data: Operation[]) =>
            patch<Operation[], AppSettings>({ url: `${Endpoints.AppSettings}/${tenantId}`, data }),
        queryKey: queryKeys.appSettings.tenant(tenantId),
    });

    return useMemo<IUseAppSettingsQuery>(
        () => ({
            myAppSettingsQuery,
            tenantAppSettingsQuery,
            createMutation,
            updateMutation,
        }),
        [myAppSettingsQuery, tenantAppSettingsQuery, createMutation, updateMutation]
    );
};
